import { Loader2, Search, User } from "lucide-react";
import { cn } from "@/lib/cn";
import type { ProductionViewMode } from "../production-types";

type Props = {
  busca: string;
  onBuscaChange: (v: string) => void;
  dentistas: string[];
  dentistaFiltro: string;
  onDentistaChange: (v: string) => void;
  clinicas: string[];
  clinicaFiltro: string;
  onClinicaChange: (v: string) => void;
  apenasMeus: boolean;
  onApenasMeusChange: (v: boolean) => void;
  visao: ProductionViewMode;
  onVisaoChange: (v: ProductionViewMode) => void;
  carregando: boolean;
};

const CAMPO =
  "h-11 w-full min-w-0 rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-700 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground";

/**
 * Controles superiores da Produção no celular.
 *
 * Sete controles não cabem numa linha em 360px, e empilhá-los soltos fragmentava a tela. Aqui eles
 * ganham hierarquia: busca em cima, os dois seletores dividindo a linha, e uma linha final com
 * "Meus trabalhos" à esquerda e a segmentação de visão à direita, sob o rótulo VISUALIZAÇÃO.
 *
 * O rótulo pertence só à segmentação — por isso ele fica sobre a coluna da direita, e não sobre a
 * linha inteira: "Meus trabalhos" é filtro, não visualização.
 */
export function MobileProductionToolbar({
  busca,
  onBuscaChange,
  dentistas,
  dentistaFiltro,
  onDentistaChange,
  clinicas,
  clinicaFiltro,
  onClinicaChange,
  apenasMeus,
  onApenasMeusChange,
  visao,
  onVisaoChange,
  carregando,
}: Props) {
  return (
    <div className="mb-3 flex flex-col gap-2 md:hidden">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
        <input
          type="search"
          value={busca}
          onChange={(e) => onBuscaChange(e.target.value)}
          placeholder="Buscar clínica, paciente, dentista, serviço..."
          className={cn(CAMPO, "pl-9 pr-9 text-base")}
        />
        {carregando && (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-zinc-400" />
        )}
      </div>

      {/* Dividem a linha enquanto couberem; abaixo de ~140px cada um, empilham sozinhos. */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(8.75rem,1fr))] gap-2">
        <select
          value={dentistaFiltro}
          onChange={(e) => onDentistaChange(e.target.value)}
          aria-label="Filtrar por dentista"
          className={CAMPO}
        >
          <option value="">Todos os dentistas</option>
          {dentistas.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <select
          value={clinicaFiltro}
          onChange={(e) => onClinicaChange(e.target.value)}
          aria-label="Filtrar por clínica"
          className={CAMPO}
        >
          <option value="">Todas as clínicas</option>
          {clinicas.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 items-end gap-2">
        <button
          type="button"
          data-tour="producao-meus-trabalhos"
          onClick={() => onApenasMeusChange(!apenasMeus)}
          aria-pressed={apenasMeus}
          className={cn(
            "inline-flex h-11 w-full min-w-0 items-center justify-center gap-1.5 rounded-md border px-2 text-[clamp(11px,3.2vw,13px)] font-semibold",
            apenasMeus
              ? "border-brand-300 bg-brand-50 text-brand-700 dark:border-primary/40 dark:bg-primary/10 dark:text-primary"
              : "border-zinc-200 bg-white text-zinc-600 dark:border-border dark:bg-card dark:text-muted-foreground",
          )}
        >
          <User className="h-4 w-4 shrink-0" />
          <span className="truncate">Meus trabalhos</span>
        </button>

        <div className="flex min-w-0 flex-col gap-1.5">
          <span
            id="rotulo-visualizacao"
            className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400 dark:text-muted-foreground"
          >
            Visualização
          </span>
          <div
            role="tablist"
            aria-labelledby="rotulo-visualizacao"
            className="flex h-[46px] items-center overflow-hidden rounded-md border border-zinc-200 bg-white dark:border-border dark:bg-card"
          >
            {(
              [
                { key: "estagios", label: "Estágios" },
                { key: "planilha", label: "Planilha" },
              ] as const
            ).map(({ key, label }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={visao === key}
                onClick={() => onVisaoChange(key)}
                className={cn(
                  "inline-flex h-full min-w-0 flex-1 basis-0 items-center justify-center px-1 text-[clamp(11px,3.2vw,13px)] transition-colors",
                  visao === key
                    ? "bg-brand-600 font-semibold text-white"
                    : "font-medium text-zinc-600 dark:text-muted-foreground",
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
